import { Link } from "react-router-dom";
import { Mail, Phone, Globe, Rss } from "lucide-react";
import NewsletterSubscribe from "./NewsletterSubscribe";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative bg-brand-navy text-white mt-16">
      <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-16">
        {/* Newsletter capture — full-width band above the sitemap columns.
            Rendered once here so bots see a subscribe form on every page
            without a duplicate card cluttering the contact strip. */}
        <div className="mb-14 grid md:grid-cols-5 gap-6 items-center rounded-md border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 sm:p-8">
          <div className="md:col-span-3">
            <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.24em] text-brand-electric">
              <Rss className="h-3 w-3" /> Blog · Never miss a guide
            </div>
            <h3 className="mt-3 font-display font-black text-white text-xl sm:text-2xl leading-snug">
              Fresh licensing guides, straight to your inbox.
            </h3>
            <p className="mt-2 text-sm text-white/70 max-w-xl leading-relaxed">
              One email whenever we publish a new Microsoft 365, Autodesk, Adobe or Windows Server buyer's guide. No spam, unsubscribe anytime. Prefer feeds? Grab the{" "}
              <a
                href="/resources/blog/rss.xml"
                data-testid="footer-newsletter-rss-inline"
                className="underline decoration-brand-electric underline-offset-2 hover:text-white"
              >
                RSS feed
              </a>.
            </p>
          </div>
          <div className="md:col-span-2">
            <NewsletterSubscribe source="footer" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-1">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="India Enterprises" width="180" height="44" decoding="async" className="h-11 w-auto bg-white rounded-sm p-1" />
              <div>
                <div className="font-display font-black text-sm tracking-tight">INDIA ENTERPRISES</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/60">Authorized Solution Specialist</div>
              </div>
            </div>
            <p className="mt-6 text-sm text-white/70 leading-relaxed">
              Premier enterprise software distribution pipeline for verified compliance assets and structured technology designs.
            </p>
            <p className="mt-4 text-xs font-mono text-brand-electric tracking-wider">
              "Certified technology, delivered with precision."
            </p>
          </div>

          <div>
            <h4 className="font-display font-bold text-sm uppercase tracking-[0.2em] text-white/50 mb-4">Navigate</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/" className="text-white/80 hover:text-brand-electric transition-colors">Home</Link></li>
              <li><Link to="/services" className="text-white/80 hover:text-brand-electric transition-colors">Services</Link></li>
              <li><Link to="/partners" className="text-white/80 hover:text-brand-electric transition-colors">Partners</Link></li>
              <li><Link to="/about" className="text-white/80 hover:text-brand-electric transition-colors">About</Link></li>
              <li><Link to="/contact" className="text-white/80 hover:text-brand-electric transition-colors">Contact</Link></li>
              <li><Link to="/sitemap" className="text-white/80 hover:text-brand-electric transition-colors" data-testid="footer-sitemap-link">Sitemap</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-bold text-sm uppercase tracking-[0.2em] text-white/50 mb-4">Products &amp; Services</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/products?brand=Microsoft" className="text-white/80 hover:text-brand-electric transition-colors">Microsoft</Link></li>
              <li><Link to="/autodesk" className="text-white/80 hover:text-brand-electric transition-colors">Autodesk</Link></li>
              <li><Link to="/adobe" className="text-white/80 hover:text-brand-electric transition-colors">Adobe</Link></li>
              <li><Link to="/corel" className="text-white/80 hover:text-brand-electric transition-colors">Corel</Link></li>
              <li><Link to="/sketchup" className="text-white/80 hover:text-brand-electric transition-colors">SketchUp</Link></li>
              <li><Link to="/zoho" className="text-white/80 hover:text-brand-electric transition-colors">Zoho</Link></li>
              <li><Link to="/hardware" className="text-white/80 hover:text-brand-electric transition-colors">HPE Hardware</Link></li>
              <li><Link to="/products?brand=Microsoft%20Open" className="text-white/80 hover:text-brand-electric transition-colors">Open Value</Link></li>
              <li><Link to="/microsoft-azure" className="text-white/80 hover:text-brand-electric transition-colors">Microsoft Azure</Link></li>
              <li><Link to="/azure-calculator" className="text-white/80 hover:text-brand-electric transition-colors">Azure PAYG Calculator</Link></li>
              <li><Link to="/managed-services" className="text-white/80 hover:text-brand-electric transition-colors">Managed Services</Link></li>
              <li><Link to="/software-web-development" className="text-white/80 hover:text-brand-electric transition-colors">Web &amp; Software Development</Link></li>
              <li><Link to="/estimate" className="text-white/80 hover:text-brand-electric transition-colors">Get Estimate</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-bold text-sm uppercase tracking-[0.2em] text-white/50 mb-4">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-brand-electric" />
                <a href="tel:+918527694100" className="text-white/85 hover:text-brand-electric">+91 85276 94100</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-4 w-4 mt-0.5 text-brand-electric" />
                <a href="mailto:sales@indiaenterprises.co" className="text-white/85 hover:text-brand-electric break-all">sales@indiaenterprises.co</a>
              </li>
              <li className="flex items-start gap-3">
                <Globe className="h-4 w-4 mt-0.5 text-brand-electric" />
                <span className="text-white/85">indiaenterprises.co</span>
              </li>
              <li className="flex items-start gap-3">
                <Rss className="h-4 w-4 mt-0.5 text-brand-electric" />
                <a
                  href="/resources/blog/rss.xml"
                  data-testid="footer-rss-link"
                  className="text-white/85 hover:text-brand-electric"
                >
                  Blog RSS Feed
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-white/50 font-mono">
            © {new Date().getFullYear()} India Enterprises. All rights reserved.
          </p>
          <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
            <li>
              <Link
                to="/return-policy"
                data-testid="footer-return-policy-link"
                className="text-white/70 hover:text-brand-electric transition-colors"
              >
                Return Policy
              </Link>
            </li>
            <li>
              <Link
                to="/shipping-policy"
                data-testid="footer-shipping-policy-link"
                className="text-white/70 hover:text-brand-electric transition-colors"
              >
                Shipping
              </Link>
            </li>
            <li>
              <Link
                to="/privacy"
                data-testid="footer-privacy-link"
                className="text-white/70 hover:text-brand-electric transition-colors"
              >
                Privacy
              </Link>
            </li>
            <li>
              <Link
                to="/terms"
                data-testid="footer-terms-link"
                className="text-white/70 hover:text-brand-electric transition-colors"
              >
                Terms
              </Link>
            </li>
            <li>
              <Link
                to="/contact"
                className="text-white/70 hover:text-brand-electric transition-colors"
              >
                Contact
              </Link>
            </li>
            <li>
              <Link
                to="/sitemap"
                className="text-white/70 hover:text-brand-electric transition-colors"
              >
                Sitemap
              </Link>
            </li>
          </ul>
        </div>
        <p data-testid="footer-gst-note" className="mt-3 text-[11px] text-brand-electric/70 font-mono text-center md:text-left">
          All product & service prices exclude GST. 18% GST will be applied at the time of billing.
        </p>
      </div>
    </footer>
  );
}
